// Core
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { AnchorNav, Button, Chip } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetResourcesByCommunityIdQuery,
  useUpdateResourceByIdMutation,
} from "state/resources/resourcesApiSlice";

// Utils
import { handleUpdateAsset } from "utils/crud/update";
import { resourceVisual } from "utils/resourceTypes";

// Sections
import GeneralInfo from "pages/ManageResources/Single/GeneralInfo";
import Details from "./Details";
import MetersInfo from "./Meters";
import OwnershipsSection from "./Ownerships";

function SingleResource() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // READ resource
  const { id } = useParams();
  const community = useSelector(selectCurrentCommunity);
  const { resource } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ resource: data?.entities[id] }),
    }
  );

  const { t } = useTranslation(["resources", "community"]);

  // UPDATE resource
  const [updateResource, { isLoading }] = useUpdateResourceByIdMutation();
  const onUpdateResource = async (data, message) => {
    // Only send what we're changing
    const newData = Object.keys(data).reduce((acc, key) => {
      // Always send details, type, and ownerships_payload: nested/replace-all fields that can't be diffed against the resource object
      if (key === "details" || key === "type" || key === "ownerships_payload") {
        acc[key] = data[key];
        return acc;
      }

      if (data[key] !== resource[key]) {
        acc[key] = data[key];
      }
      return acc;
    }, {});

    handleUpdateAsset(updateResource, community.id, id, newData, dispatch, message);
  };

  const onUpdateMeter = async ({ meterId }) => {
    const newResource = { meter_id: meterId };

    const action = () =>
      handleUpdateAsset(
        updateResource,
        community.id,
        id,
        newResource,
        dispatch,
        t("single.meters.updated")
      );

    if (meterId === "TO_REMOVE") {
      dispatch(
        openWarningAlert({
          confirmText: t("single.confirm_remove"),
          confirmAction: action,
        })
      );
    } else {
      action();
    }
  };

  const navItems = [
    { id: "general", icon: "badge", label: t("single.general.title") },
    { id: "details", icon: "tune", label: t("single.details.title") },
    { id: "meters", icon: "electric_meter", label: t("single.meters.title") },
    { id: "ownerships", icon: "groups", label: t("single.ownerships.title") },
  ];

  const backToResources = () => navigate(`/communities/${community.id}/infrastructure/resources`);

  const visual = resource ? resourceVisual(resource.type) : null;
  const typeLabel = resource
    ? t(`new_resource.selects.types.${resource.type}`, { defaultValue: resource.type })
    : "";

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="resource-edit" />
      <div className="rec-crumbs">
        <Icon name="home" />
        <span>{community?.name || "—"}</span>
        <Icon name="chevron_right" />
        <button
          type="button"
          className="bg-transparent border-0 p-0 cursor-pointer text-inherit font-sans text-[12.5px]"
          onClick={backToResources}
        >
          {t("title")}
        </button>
        <Icon name="chevron_right" />
        <b>{resource?.name || "—"}</b>
      </div>

      <div className="rec-page-head">
        <Button variant="ghost" size="sm" onClick={backToResources}>
          <Icon name="arrow_back" />
          {t("single.back_button", { defaultValue: "Voltar aos recursos" })}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] items-start gap-rec">
        <AnchorNav items={navItems} />

        <div className="flex flex-col gap-rec min-w-0">
          {resource && visual && (
            <div className="rec-card rec-card-pad">
              <div className="rec-profile-hero">
                <div className={`rec-av-lg ${visual.tint}`}>
                  <Icon name={visual.icon} fill style={{ fontSize: 30 }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-[10px] flex-wrap">
                    <h2 className="m-0 text-[21px] font-bold text-ink tracking-[-0.3px]">
                      {resource.name}
                    </h2>
                    <Chip tone="gray">{typeLabel}</Chip>
                    {resource.status === "ACTIVE" ? (
                      <Chip tone="green">{t("table.badges.active")}</Chip>
                    ) : (
                      <Chip tone="red">{t("table.badges.inactive")}</Chip>
                    )}
                  </div>
                  {resource.meter && (
                    <div className="mt-[6px] text-ink-3 text-[13.5px] inline-flex items-center gap-[6px]">
                      <Icon name="electric_meter" style={{ fontSize: 16 }} />
                      {`${t("single.meters.title")}: ${resource.meter.identifier}`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {resource && (
            <GeneralInfo data={resource} onUpdate={onUpdateResource} isLoading={isLoading} />
          )}
          {resource && (
            <Details data={resource} onUpdate={onUpdateResource} isLoading={isLoading} />
          )}
          {resource && (
            <MetersInfo data={resource.meter_id ? resource.meter : null} onUpdate={onUpdateMeter} />
          )}
          {resource && (
            <OwnershipsSection data={resource} onUpdate={onUpdateResource} isLoading={isLoading} />
          )}
        </div>
      </div>
    </div>
  );
}

export default SingleResource;
